import React from "react";
import ReactDOM from "react-dom";
import FeatherIcon from "feather-icons-react";
import "./CustomModal.css";
import { useBottomSheet } from "../../hooks/useBottomSheet";
import { useIsWideViewport } from "../../hooks/useIsWideViewport";
import { useIsHiddenForPreview } from "../../contexts/PreviewHiddenContext";

// A bottom-docked panel (Google Maps-style) - unlike CustomModal, it never
// dims/blocks the page behind it, and the page underneath stays scrollable
// and clickable the whole time since there's no overlay. Portal'd straight
// to document.body (like react-modal does for CustomModal) so its fixed
// positioning and z-index aren't at the mercy of some ancestor's own
// position/overflow/z-index.
//
// isOpen/onClose are a real controlled pair now, owned by the caller (e.g.
// expandMatch setting isOpen=true alongside bumping expandSignal) - on
// both mobile and desktop this component simply doesn't render anything at
// all while closed. It used to stay mounted permanently instead, resting
// as a small always-visible peek (a bottom sliver on mobile, a docked edge
// tab on desktop) so there was always something to tap open - that peek
// turned out to be a real cost, not just a discoverability nicety: on
// desktop it permanently reserved a .page_container right-margin (see
// index.css's old body:has(.bottom-sheet-panel-docked-collapsed) rule)
// that a wide-viewport screenshot export baked in even when nobody had
// ever opened the sheet, and on mobile it permanently reserved bottom
// padding on every page that used it (.bottom-sheet-page-spacer) for a
// strip that mostly just sat there unused. Discoverability now comes from
// each caller's own trigger (clicking a match row bumps expandSignal,
// which is what actually opens this) rather than a permanent handle.
//
// title/icon render in the handle zone rather than the caller's own
// scrolling <h2> - that h2 lives below the fold until scrolled to, so a
// bare content area read as unlabeled. Putting the title in the always-
// visible handle zone instead makes the open sheet read as a labeled tab
// (e.g. "Matches"), not just a content blob with no header.
// interactive=false keeps the sheet visible (no layout shift) but lets
// clicks/taps pass straight through it to whatever's underneath - e.g.
// Schedule's pick-and-place move, where the destination cell the user
// needs to tap can land underneath this fixed, full-width sheet.
export const BottomSheetPanel = ({ isOpen = true, onClose, interactive = true, expandSignal, scrollTargetId, title, icon = "list", children }) => {
    const { sheetRef, handleRef } = useBottomSheet({ expandSignal, scrollTargetId, onClose });
    // Reads window.innerWidth of whatever window this instance is actually
    // rendering in - the real outer page normally, but the "Screen size"
    // preview iframe's OWN window when this is the framed copy (a same-
    // origin iframe is its own independent browsing context, so this isn't
    // affected by the outer page's own real width at all). That's exactly
    // what previewing a Tablet/Laptop preset should do: show the docked
    // layout a real visitor would get at that width, same as every other
    // piece of Tournament's own content already reflows per preset.
    const isWide = useIsWideViewport();
    const isHiddenForPreview = useIsHiddenForPreview();

    // display:none on an ancestor (App.js hiding this whole Tournament copy
    // behind the "Screen size" preview) has no effect on this component's
    // own portal, which renders straight to document.body regardless of
    // where it's nested - without this check, a background instance's own
    // sheet stayed visibly floating on top of the real preview, a second
    // "shtorka" on top of the one correctly showing inside the preview
    // iframe. See PreviewHiddenContext.js. (The framed copy inside the
    // preview iframe itself DOES still show this sheet, same as any other
    // page content - it's a single live instance with its own normal
    // fetch/socket connection, not a second one mirroring the real page's
    // state, so there's no data-sync risk from showing it there too.)
    if (!isOpen || isHiddenForPreview) return null;

    if (isWide) {
        return ReactDOM.createPortal(
            <div
                className="modal-content-sheet bottom-sheet-panel bottom-sheet-panel-docked bottom-sheet-panel-docked-expanded"
                style={interactive ? undefined : { pointerEvents: "none" }}
            >
                <div className="bottom-sheet-panel-docked-scroll">
                    <div className="modal-drag-handle-zone" onClick={onClose}>
                        {title && (
                            <div className="bottom-sheet-title">
                                <FeatherIcon icon={icon} /> {title}
                            </div>
                        )}
                        <FeatherIcon icon="x" className="bottom-sheet-dock-collapse-icon" />
                    </div>
                    {children}
                </div>
            </div>,
            document.body
        );
    }

    return ReactDOM.createPortal(
        <div className="modal-content-sheet bottom-sheet-panel" style={interactive ? undefined : { pointerEvents: "none" }} ref={sheetRef}>
            <div className="modal-drag-handle-zone" ref={handleRef}>
                <div className="modal-drag-handle" />
                {title && (
                    <div className="bottom-sheet-title">
                        <FeatherIcon icon={icon} /> {title}
                    </div>
                )}
            </div>
            {children}
        </div>,
        document.body
    );
};
